/**
 * Push notification subscription management.
 *
 * Handles service worker registration, push subscription via Web Push API,
 * and subscription storage in Supabase.
 */

import { captureException } from "./sentry";
import { supabase } from "./supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PushSubscriptionRecord {
  id: string;
  user_id: string;
  endpoint: string;
  p256dh: string;
  auth_key: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Service Worker Registration
// ---------------------------------------------------------------------------

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    return registration;
  } catch (err) {
    void captureException(err, { tags: { module: "push-notifications" } });
    return null;
  }
}

export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ?? null;
}

// ---------------------------------------------------------------------------
// Push Subscription
// ---------------------------------------------------------------------------

/**
 * Get the VAPID public key from the Supabase Edge Function.
 * The key is stored in Doppler and exposed via the vapid-public-key function.
 */
export async function getVapidPublicKey(): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke("vapid-public-key");
  if (error || !data?.publicKey) {
    void captureException(error, { tags: { module: "push-notifications" } });
    return null;
  }
  return data.publicKey;
}

/**
 * Convert a base64 VAPID key to Uint8Array for the pushManager.subscribe call.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications. Returns the subscription or null on failure.
 */
export async function subscribeToPush(
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string,
): Promise<PushSubscription | null> {
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
    });
    return subscription;
  } catch (err) {
    void captureException(err, { tags: { module: "push-notifications" } });
    return null;
  }
}

/**
 * Get the current push subscription, if any.
 */
export async function getCurrentPushSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  return registration.pushManager.getSubscription();
}

// ---------------------------------------------------------------------------
// Supabase Storage
// ---------------------------------------------------------------------------

/**
 * Save a push subscription to Supabase for the current user.
 */
export async function savePushSubscription(
  userId: string,
  subscription: PushSubscription,
): Promise<void> {
  const json = subscription.toJSON();
  const keys = json.keys ?? {};

  const { error } = await supabase.from("user_push_subscriptions").upsert(
    {
      user_id: userId,
      endpoint: json.endpoint,
      p256dh: keys.p256dh ?? "",
      auth_key: keys.auth ?? "",
      user_agent: navigator.userAgent,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,endpoint" },
  );

  if (error) throw new Error(`Failed to save push subscription: ${error.message}`);
}

/**
 * Remove a push subscription from Supabase.
 */
export async function deletePushSubscription(userId: string, endpoint: string): Promise<void> {
  const { error } = await supabase
    .from("user_push_subscriptions")
    .delete()
    .eq("user_id", userId)
    .eq("endpoint", endpoint);

  if (error) throw new Error(`Failed to delete push subscription: ${error.message}`);
}

/**
 * Unsubscribe from push notifications: remove browser subscription + DB record.
 */
export async function unsubscribeFromPush(
  userId: string,
  registration: ServiceWorkerRegistration,
): Promise<void> {
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await deletePushSubscription(userId, endpoint);
  }
}

// ---------------------------------------------------------------------------
// Notification Permission
// ---------------------------------------------------------------------------

export function getNotificationPermission(): NotificationPermission {
  if (!("Notification" in window)) return "denied";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) return "denied";
  return Notification.requestPermission();
}

// ---------------------------------------------------------------------------
// Preferences (push_enabled flag in notification_preferences)
// ---------------------------------------------------------------------------

export async function getPushEnabled(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("push_enabled")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    void captureException(error, { tags: { module: "push-notifications" } });
    return false;
  }
  return data?.push_enabled ?? false;
}

export async function setPushEnabled(userId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.from("notification_preferences").upsert(
    {
      user_id: userId,
      push_enabled: enabled,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id" },
  );

  if (error) throw new Error(`Failed to save push preference: ${error.message}`);
}
